import { useCallback, useEffect, useState } from 'react';

import {
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} from '@app/api/Notifications';
import { WARNING } from '@app/constants/alerts';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  applyPopupNotificationsEnabled,
  readPopupNotificationsEnabled,
  selectPopupNotificationsEnabled,
  writePopupNotificationsEnabled,
} from '@app/shared/components/NotificationsBell/lib';

// Состояние свича «Отображение всплывающих уведомлений» в окне
// «Уведомления». Источник истины — бэкенд (GetNotificationSettings,
// матрица «событие × канал»); свич — агрегат по каналу in-app. В
// localStorage держим зеркало: realtime-хук показа тоста читает его
// синхронно в момент показа, поэтому общий React-стейт с ним не нужен.
export const usePopupNotificationsSetting = () => {
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const { data } = useGetNotificationSettingsQuery(
    { ActiveBranchId: activeBranchId },
    { skip: !activeBranchId }
  );
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateNotificationSettingsMutation();

  const [isEnabled, setIsEnabled] = useState<boolean>(
    readPopupNotificationsEnabled
  );

  // Пришёл ответ бэка — синхронизируем локальный стейт и зеркало.
  useEffect(() => {
    if (data) {
      const enabled = selectPopupNotificationsEnabled(data);
      setIsEnabled(enabled);
      writePopupNotificationsEnabled(enabled);
    }
  }, [data]);

  const changeEnabled = useCallback(
    async (nextEnabled: boolean) => {
      // Без загруженной матрицы тело для UpdateNotificationSettings не
      // собрать — свич в UI до этого момента заблокирован (isReady), так что
      // локальный стейт и зеркало без реального сохранения не разъезжаются.
      if (!data || !activeBranchId) return;

      const prevEnabled = isEnabled;
      // Стейт и зеркало двигаем сразу (зеркало читает realtime-хук в момент
      // показа тоста); при ошибке сохранения откатываем оба и показываем
      // предупреждение — иначе свич и бэкенд молча разойдутся.
      setIsEnabled(nextEnabled);
      writePopupNotificationsEnabled(nextEnabled);

      try {
        await updateSettings({
          ActiveBranchId: activeBranchId,
          data: applyPopupNotificationsEnabled(data, nextEnabled),
        }).unwrap();
      } catch (error) {
        setIsEnabled(prevEnabled);
        writePopupNotificationsEnabled(prevEnabled);
        showNotification(error, WARNING);
      }
    },
    [data, activeBranchId, isEnabled, updateSettings, showNotification]
  );

  return { isEnabled, changeEnabled, isSaving, isReady: Boolean(data) };
};
